import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import type { AccountSummaryDto, PagedResult } from "@/lib/clients-accounts";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandItem,
  CommandEmpty,
} from "@/components/ui/command";

export interface AccountPickerProps {
  /** The clientId to scope the account list. When null/undefined the picker is disabled. */
  clientId: number | null | undefined;
  value: number | null;
  onChange: (accountId: number | null) => void;
  placeholder?: string;
}

export function AccountPicker({
  clientId,
  value,
  onChange,
  placeholder,
}: AccountPickerProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const disabled = clientId == null;

  const { data } = useQuery({
    queryKey: ["account-picker", clientId, q],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("clientId", String(clientId));
      params.set("pageSize", "20");
      if (q.trim().length > 0) params.set("q", q.trim());
      return apiFetch<PagedResult<AccountSummaryDto>>(`/api/accounts?${params.toString()}`);
    },
    enabled: !disabled,
    staleTime: 5000,
    select: (res) => res.items,
  });

  // Find the currently selected account in the fetched list for display.
  const selectedAccount = data?.find((a) => a.accountId === value) ?? null;

  if (disabled) {
    return (
      <div
        className="flex h-9 w-full items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-400 cursor-not-allowed select-none"
        aria-disabled="true"
      >
        {placeholder ?? "Select a client first"}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={placeholder ?? "Search accounts..."}
          className="w-full justify-between h-9 px-3 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-normal shadow-none"
        >
          <span className="truncate">
            {selectedAccount ? `${selectedAccount.accountReferenceNo} (${selectedAccount.companyName || "No Company"})` : (placeholder ?? "Search accounts...")}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[300px]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder ?? "Search accounts..."}
            value={q}
            onValueChange={setQ}
          />
          <CommandList className="max-h-[200px] overflow-y-auto">
            {data && data.length === 0 && <CommandEmpty>No accounts found</CommandEmpty>}
            {data?.map((a) => (
              <CommandItem
                key={a.accountId}
                onSelect={() => {
                  onChange(a.accountId);
                  setOpen(false);
                }}
                className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 px-2 py-1.5 text-sm"
              >
                <span className="font-mono font-semibold text-slate-700">{a.accountReferenceNo}</span>
                {a.companyName && (
                  <span className="text-slate-400 text-xs">({a.companyName})</span>
                )}
                {a.accountId === value && <Check className="ml-auto h-4 w-4 text-primary" />}
              </CommandItem>
            ))}
          </CommandList>
          {value !== null && (
            <div className="border-t border-slate-100 p-2 text-xs flex justify-between items-center bg-slate-50/50">
              <span className="text-slate-500">
                Selected: <strong className="font-mono text-slate-700">{selectedAccount?.accountReferenceNo ?? `#${value}`}</strong>
              </span>
              <button
                className="text-red-500 hover:underline font-semibold"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                Clear
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
